import mediaManagerTpl from 'templates/carousels/highlightCarousel/mediaManager.tpl';
import VideoPreview from '../videoPreview/videoPreview';
import { checkAutoplayCapabilities } from '../videoPreview/videoPreviewCapabilities';
import { VideoPreviewEvent, LocalStorageName } from '../videoPreview/videoPreviewConstants';
import { getLocale } from 'services/LocalizationService';
export default class MediaManager {
    #videoUrl;
    #videoLogoUrl;
    #imageUrl;
    #fallbackText;
    #template;
    #video;
    #mediaManagerContainer;
    #imageContainer;
    #mediaContainer;
    #overlayContainer;
    #overlayTimeout;
    #target;
    #volumeControl;
    #altImage;
    #timeOut;
    #muted;
    #onEndedCallback;
    #onPlayingCallback;
    #onMuteCallback;
    #onUnmuteCallback;
    #showVideoOverlayCallback;
    #videoContentId;
    #isLive;
    #isUpcoming;
    #scheduleBadge;
    #scheduleBadgeText;
    #isEvent;
    isProminent = false;
    #hlsVideoUrl = '';
    #previewContainer;
    #channelName;

    constructor({
        target,
        videoContentId,
        videoLogoUrl,
        imageUrl,
        fallbackText,
        altImage,
        isLive,
        isUpcoming,
        scheduleBadgeText,
        isEvent,
        previewContainer,
        hlsVideoUrl,
        scheduleBadge,
        channelName,
    }) {
        this.#target = target;
        this.#videoLogoUrl = videoLogoUrl;
        this.#imageUrl = imageUrl;
        this.#altImage = altImage;
        this.#fallbackText = fallbackText;
        this.#videoContentId = videoContentId;
        this.#isLive = isLive;
        this.#isUpcoming = isUpcoming;
        this.#scheduleBadge = scheduleBadge;
        this.#scheduleBadgeText = scheduleBadgeText;
        this.#isEvent = isEvent;
        this.#channelName = channelName;

        this.hlsVideoUrl = hlsVideoUrl;
        this.#previewContainer = previewContainer;
        this.createTemplate();

        this.#mediaManagerContainer = this.#target.querySelector('.media-manager');
        this.#imageContainer = this.#target.querySelector('.media-manager-image');
        let img_el = this.#imageContainer.querySelector('img');
        img_el.src = img_el.getAttribute('content');
        this.#mediaContainer = this.#target.querySelector('.media-manager-video');
        this.#overlayContainer = this.#target.querySelector('.media-info');
        this.#volumeControl = this.#overlayContainer.querySelector('.sound-control');
    }

    handleVideoUrl() {
        if (!this.#videoContentId) {
            return '';
        }

        if (this.useHLS()) {
            return this.#hlsVideoUrl;
        }

        return `https://splice.amlg.io/api/v2/video/${this.#videoContentId}/preview/high69.mp4`;
    }

    useHLS() {
        return CBS.Registry.Features.hls_video_preview_enabled;
    }

    createTemplate() {
        this.#template = mediaManagerTpl({
            videoUrl: this.#videoUrl,
            videoLogoUrl: this.#videoLogoUrl,
            imageUrl: this.#imageUrl,
            imageAlt: this.#altImage,
            fallbackText: this.#fallbackText,
            isLive: this.#isLive,
            liveCopy: getLocale('live') ?? 'LIVE',
            isUpcoming: this.#isUpcoming,
            scheduleBadgeText: this.#scheduleBadgeText,
            isEvent: this.#isEvent,
            previewContainer: this.#previewContainer,
            scheduleBadge: this.#scheduleBadge,
            channelName: this.#channelName,
        });

        this.#target.innerHTML = this.#template;
    }

    async init() {
        this.#videoUrl = this.handleVideoUrl();
        if (this.hasVideo()) {
            try {
                await checkAutoplayCapabilities();
                let spliceVolumeOn = localStorage.getItem(LocalStorageName.VOLUME_ON) === '1';
                this.#muted = !spliceVolumeOn;
                let delayDuration = 1000;

                const hpAbTestDelayActiveVariant =
                    CBS?.AbTestsConfigs?.modules?.HomePageMinimizePreviewDelay?.activeVariant;
                switch (hpAbTestDelayActiveVariant) {
                    case '0_sec':
                        delayDuration = 0;
                        break;
                    case '0_5_sec':
                        delayDuration = 500;
                        break;
                    case '1_sec':
                    case 'control':
                        delayDuration = 1000;
                        break;
                    case '2_sec':
                        delayDuration = 2000;
                        break;
                }

                this.#video = new VideoPreview({
                    id: this.#videoContentId,
                    selector: `#${this.#previewContainer}`,
                    delayStart: delayDuration,
                    mediaUrl: this.#videoUrl,
                    destroyAfterPlay: false,
                    mutedAutoplay: this.#muted,
                });

                await this.#video.setPlayer();
                this.__handleInitialVolume();
                this.addVideoListeners();

                if (this.#mediaManagerContainer.classList.contains('prominent')) {
                    this.isProminent = true;
                }
            } catch (error) {
                console.log(error);
                this.addClass('no-video');
            }
        } else {
            this.addClass('no-video');
        }
    }

    __handleInitialVolume() {
        if (this.#volumeControl) {
            if (!this.#muted) {
                this.#volumeControl.classList.add('unmuted');
            } else {
                this.#volumeControl.classList.remove('unmuted');
            }
        }
    }

    addVideoListeners() {
        this.#video.addEventListener(VideoPreviewEvent.PLAYBACK_STARTED, this.videoPlayingHandler);

        this.#video.addEventListener(VideoPreviewEvent.ENDED, this.videoEndedHandler);
        this.#mediaContainer.addEventListener('mousemove', this.videoMouseMoveHandler);
        if (this.#volumeControl) {
            this.#volumeControl.addEventListener('click', this.volumeControlHandler);
        }
    }

    removeVideoListeners() {
        this.#video.removeEventListener(
            VideoPreviewEvent.PLAYBACK_STARTED,
            this.videoPlayingHandler,
        );

        this.#video.removeEventListener(VideoPreviewEvent.ENDED, this.videoEndedHandler);
        this.#mediaContainer.removeEventListener('mousemove', this.videoMouseMoveHandler);
        if (this.#volumeControl) {
            this.#volumeControl.removeEventListener('click', this.volumeControlHandler);
        }
    }

    volumeControlHandler = () => {
        this.toggleVolume();
        this.videoMouseMoveHandler();
    };

    toggleVolume = () => {
        this.#muted = !this.#muted;
        this.#video.mute(this.#muted);

        if (!this.#muted) {
            this.#volumeControl.classList.add('unmuted');
            this.#onUnmuteCallback();
        } else {
            this.#volumeControl.classList.remove('unmuted');
            this.#onMuteCallback();
        }
    };

    videoMouseMoveHandler = () => {
        if (this.#overlayContainer.classList.contains('show')) {
            clearTimeout(this.#overlayTimeout);
        }

        this.showVideoOverlay();
    };

    videoPlayingHandler = () => {
        this.#imageContainer.classList.add('hide');
        this.showVideoOverlay();
        this.#onPlayingCallback();
    };

    videoEndedHandler = (e) => {
        this.#video.pause();
        clearTimeout(this.#overlayTimeout);
        this.#imageContainer.classList.add('show');
        this.#imageContainer.classList.remove('hide');

        if (!this.isProminent) {
            this.#overlayContainer.classList.remove('show');
        }
        this.#onEndedCallback(e);
    };

    showVideoOverlay() {
        this.#overlayContainer.classList.add('show');
        this.#overlayTimeout = setTimeout(() => {
            if (!this.isProminent) {
                this.#overlayContainer.classList.remove('show');
            }
            if (this.#showVideoOverlayCallback) {
                this.#showVideoOverlayCallback();
            }
        }, 4000);
    }

    hasVideo() {
        return this.#videoUrl !== undefined && this.#videoUrl !== null && this.#videoUrl !== '';
    }

    get video() {
        return this.#video;
    }

    get volumeControl() {
        return this.#volumeControl;
    }

    get overlayContainer() {
        return this.#overlayContainer;
    }

    get muted() {
        return this.#muted;
    }

    get mediaContainer() {
        return this.#mediaContainer;
    }

    get imageContainer() {
        return this.#imageContainer;
    }

    get videoContentId() {
        return this.#videoContentId;
    }

    set muted(value) {
        this.#muted = value;
    }

    set onEndedCallback(value) {
        this.#onEndedCallback = value;
    }

    set onPlayingCallback(value) {
        this.#onPlayingCallback = value;
    }

    set onMuteCallback(value) {
        this.#onMuteCallback = value;
    }

    set onUnmuteCallback(value) {
        this.#onUnmuteCallback = value;
    }

    set showVideoOverlayCallback(value) {
        this.#showVideoOverlayCallback = value;
    }

    set hlsVideoUrl(value) {
        this.#hlsVideoUrl = value;
    }
    set videoContentId(value) {
        this.#videoContentId = value;
    }

    async destroy() {
        if (this.hasVideo()) {
            this.removeVideoListeners();
            await this.#video.removePlayer();
            this.#video = null;
        }

        clearTimeout(this.#timeOut);
        clearTimeout(this.#overlayTimeout);
    }

    addClass(value) {
        this.#mediaManagerContainer.classList.add(value);
        this.#overlayContainer.classList.add(value);
    }

    removeClass(value) {
        if (!this.isProminent) {
            this.#overlayContainer.classList.remove('show');
        }
    }
}
